import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import Layout from "@/components/Layout";
import { ProtectedRoute, AdminRoute } from "@/components/Guards";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Wishlist from "@/pages/Wishlist";
import Checkout from "@/pages/Checkout";
import OrderConfirmation from "@/pages/OrderConfirmation";
import Orders from "@/pages/Orders";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Contact from "@/pages/Contact";
import { About, Faqs, Terms, Privacy, Shipping, Refund } from "@/pages/Static";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminSaleBanners from "@/pages/admin/AdminSaleBanners";
import AdminChangePassword from "@/pages/admin/AdminChangePassword";
import AdminSiteSettings from "@/pages/admin/AdminSiteSettings";
import AdminTheme from "@/pages/admin/AdminTheme";
import AdminBanners from "@/pages/admin/AdminBanners";
import AdminBestsellers from "@/pages/admin/AdminBestsellers";
import AdminImages from "@/pages/admin/AdminImages";
import AdminSalesOverview from "@/pages/admin/AdminSalesOverview";
import { SiteProvider } from "@/contexts/SiteContext";

const withLayout = (el) => <Layout>{el}</Layout>;

function ScrollToTop() {
    const { pathname } = useLocation();
    React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
    return null;
}

export default function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <SiteProvider>
                        <CartProvider>
                        <ScrollToTop />
                        <Toaster position="top-center" toastOptions={{ style: { border: "2px solid black", borderRadius: 0, fontFamily: "JetBrains Mono, monospace" } }} />
                        <Routes>
                            <Route path="/" element={withLayout(<Home />)} />
                            <Route path="/shop" element={withLayout(<Shop />)} />
                            <Route path="/product/:id" element={withLayout(<ProductDetail />)} />
                            <Route path="/cart" element={withLayout(<Cart />)} />
                            <Route path="/wishlist" element={withLayout(<ProtectedRoute><Wishlist /></ProtectedRoute>)} />
                            <Route path="/checkout" element={withLayout(<ProtectedRoute><Checkout /></ProtectedRoute>)} />
                            <Route path="/order/:id/confirmation" element={withLayout(<ProtectedRoute><OrderConfirmation /></ProtectedRoute>)} />
                            <Route path="/orders" element={withLayout(<ProtectedRoute><Orders /></ProtectedRoute>)} />
                            <Route path="/login" element={withLayout(<Login />)} />
                            <Route path="/signup" element={withLayout(<Signup />)} />
                            <Route path="/about" element={withLayout(<About />)} />
                            <Route path="/contact" element={withLayout(<Contact />)} />
                            <Route path="/faqs" element={withLayout(<Faqs />)} />
                            <Route path="/terms" element={withLayout(<Terms />)} />
                            <Route path="/privacy" element={withLayout(<Privacy />)} />
                            <Route path="/shipping-policy" element={withLayout(<Shipping />)} />
                            <Route path="/refund-policy" element={withLayout(<Refund />)} />

                            {/* Admin */}
                            <Route path="/admin/login" element={<AdminLogin />} />
                            <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                                <Route index element={<AdminDashboard />} />
                                <Route path="site-settings" element={<AdminSiteSettings />} />
                                <Route path="theme" element={<AdminTheme />} />
                                <Route path="banners" element={<AdminBanners />} />
                                <Route path="products" element={<AdminProducts />} />
                                <Route path="categories" element={<AdminCategories />} />
                                <Route path="orders" element={<AdminOrders />} />
                                <Route path="coupons" element={<AdminCoupons />} />
                                <Route path="sale-banners" element={<AdminSaleBanners />} />
                                <Route path="sales-overview" element={<AdminSalesOverview />} />
                                <Route path="bestsellers" element={<AdminBestsellers />} />
                                <Route path="images" element={<AdminImages />} />
                                <Route path="change-password" element={<AdminChangePassword />} />
                            </Route>

                            <Route path="*" element={withLayout(<div className="p-20 text-center font-display text-4xl">404 — Lost in the Drop</div>)} />
                        </Routes>
                        </CartProvider>
                    </SiteProvider>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}
